import React, { useEffect, useMemo, useState } from 'react';
import { objectFileName, uploadFile } from '../../../../api/files';
import { useAuth } from '../../../../context/AuthContext';
import '../addons-v2.css';

function useEscapeToClose(open, onClose) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
}

const normalize = (v) => String(v ?? '').trim();

export default function AddOnEditorPage({ open, addOn, serviceId, busy, onClose, onSave }) {
  const { companyData } = useAuth();
  useEscapeToClose(open, onClose);

  const isEdit = !!addOn?.id;
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', price: 0, duration: 0, imageUrl: '' });

  useEffect(() => {
    if (!open) return;
    setUploading(false);
    const a = addOn || {};
    setForm({
      name: a.name || '',
      price: a.price || 0,
      duration: a.duration || 0,
      imageUrl: a.imageUrl || '',
    });
  }, [open, addOn]);

  const isBusy = !!busy || uploading;
  const canSubmit = useMemo(() => normalize(form.name).length > 0 && !isBusy, [form.name, isBusy]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const companyId = companyData?.id;
      const basePath = companyId ? `companies/${companyId}/addons/${serviceId}` : `addons/${serviceId}`;
      const stored = await uploadFile(file, `${basePath}/${objectFileName(file)}`);
      setForm((prev) => ({ ...prev, imageUrl: stored.url }));
    } catch (error) {
      console.error('Error uploading add-on image:', error);
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    const name = normalize(form.name);
    if (!name) return;

    const price = Math.max(0, parseFloat(String(form.price || 0)));
    const duration = Math.max(0, parseInt(String(form.duration || 0), 10));

    onSave?.({
      id: addOn?.id,
      name,
      price,
      duration,
      imageUrl: form.imageUrl || '',
      searchName: name.toLowerCase(),
      createdAt: addOn?.createdAt,
      updatedAt: new Date().toISOString(),
    });
  };

  if (!open) return null;

  return (
    <div className="ao2-page" role="region" aria-label={isEdit ? 'Edit add-on' : 'New add-on'}>
      <div className="ao2-pageHeader">
        <div style={{ minWidth: 0 }}>
          <div className="ao2-pageTitle">{isEdit ? 'Edit Add-on' : 'New Add-on'}</div>
          <div className="ao2-pageSub">Keep add-ons simple so clients can add them quickly during booking.</div>
        </div>
      </div>

      <div className="ao2-pageBody">
        <div className="ao2-formGrid">
          <section className="ao2-panel" aria-label="Image">
            <div className="ao2-field">
              <div className="ao2-label">Image</div>
              <label className="ao2-dropzone">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    handleImageUpload(file);
                    e.target.value = '';
                  }}
                  disabled={isBusy}
                />
                {form.imageUrl ? (
                  <img className="ao2-dropzoneImg" src={form.imageUrl} alt="" />
                ) : (
                  <div className="ao2-dropzoneEmpty">
                    <div className="ao2-dropzoneIcon">📷</div>
                    <div style={{ fontWeight: 800 }}>{uploading ? 'Uploading…' : 'Upload image'}</div>
                    <div className="ao2-hint">Optional</div>
                  </div>
                )}
              </label>
            </div>
          </section>

          <section className="ao2-panel" aria-label="Details">
            <div className="ao2-field">
              <label className="ao2-label" htmlFor="ao2-name">
                Add-on name
              </label>
              <input
                id="ao2-name"
                className="ao2-input2"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Beard trim"
                autoFocus
                disabled={isBusy}
              />
            </div>

            <div className="ao2-field">
              <label className="ao2-label" htmlFor="ao2-price">
                Price
              </label>
              <input
                id="ao2-price"
                className="ao2-input2"
                type="number"
                min={0}
                step="0.01"
                value={form.price}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                disabled={isBusy}
              />
            </div>

            <div className="ao2-field" style={{ marginBottom: 0 }}>
              <label className="ao2-label" htmlFor="ao2-duration">
                Duration (minutes)
              </label>
              <input
                id="ao2-duration"
                className="ao2-input2"
                type="number"
                min={0}
                step={1}
                value={form.duration}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
                disabled={isBusy}
              />
              <div className="ao2-hint">Optional — leave 0 if it doesn’t add time.</div>
            </div>
          </section>
        </div>
      </div>

      <div className="ao2-pageFooter">
        <button className="svc-btn svc-btn--ghost" type="button" onClick={onClose} disabled={isBusy}>
          Cancel
        </button>
        <button className="svc-btn svc-btn--primary" type="button" onClick={submit} disabled={!canSubmit}>
          {isBusy ? <span className="svc-spinner" /> : isEdit ? 'Save changes' : 'Add add-on'}
        </button>
      </div>
    </div>
  );
}

